import { useState, useEffect } from "react";
import axios from "axios";
import { GALLERY as DEFAULT_GALLERY } from "../lib/constants";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Gallery = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) {
          setItems(data.map((d) => ({ id: d.id, src: d.image_base64 })));
        } else {
          setItems(DEFAULT_GALLERY.map((src, i) => ({ id: `def-${i}`, src })));
        }
      })
      .catch(() => setItems(DEFAULT_GALLERY.map((src, i) => ({ id: `def-${i}`, src }))));
  }, []);

  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="relative bg-[#050505] py-24 md:py-32 noise-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="red-bar" />
              <span className="font-mono-accent text-xs text-zinc-400">03 — OUR WORK</span>
            </div>
            <h2 className="font-display font-bold uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
              Results that <br />
              <span className="chrome-text">speak</span> for themselves.
            </h2>
          </div>
          <p className="md:max-w-sm text-zinc-400 text-base leading-relaxed">
            A selection from the bay. Want to see more? Follow us on Instagram for daily before-and-afters.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {items.map((it, i) => (
            <button
              key={it.id}
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(it.src)}
              className={`relative group overflow-hidden border border-[#1A1A1A] hover:border-[#DC2626] transition-colors ${
                i === 0 ? "md:col-span-2 md:row-span-2 aspect-square" : "aspect-square"
              }`}
            >
              <img
                src={it.src}
                alt={`Detailing work ${i + 1}`}
                className="absolute inset-0 w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-4 left-4 font-mono-accent text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity">
                0{i + 1} / {items.length}
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent
          data-testid="gallery-lightbox"
          className="max-w-5xl w-full p-0 bg-black border-zinc-800 rounded-none"
          aria-describedby="gallery-lightbox-desc"
        >
          <VisuallyHidden.Root>
            <DialogTitle>Detailing work preview</DialogTitle>
            <DialogDescription id="gallery-lightbox-desc">
              Enlarged image preview of detailing work.
            </DialogDescription>
          </VisuallyHidden.Root>
          <button
            onClick={() => setActive(null)}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 z-50 bg-black/70 hover:bg-[#DC2626] text-white p-2 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          {active && <img src={active} alt="Detailing work" className="w-full h-auto object-contain" />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
